import { useRef, useState } from "react";

interface UseFilePreviewParams {
  fetchBlob: () => Promise<Blob>;
  onError?: (error: unknown) => void;
}

/**
 * Preview open/loading/blob state machine shared by both apps. Lazily fetches the blob on first open,
 * caches it, and delegates error presentation to the caller via {@link UseFilePreviewParams.onError}
 * so it carries no app-specific alert/i18n coupling.
 */
export function useFilePreview({ fetchBlob, onError }: UseFilePreviewParams) {
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  // Whether the dialog is still open when the async fetch settles. The fetch can take a moment; if the
  // user closes the preview meanwhile, we must not pop a stale error dialog afterwards.
  const isOpenRef = useRef(false);

  const closePreview = () => {
    isOpenRef.current = false;
    setIsPreviewOpen(false);
  };

  const openPreview = () => {
    isOpenRef.current = true;
    setIsPreviewOpen(true);
    if (previewBlob) {
      return;
    }
    setIsPreviewLoading(true);
    fetchBlob()
      .then((blob) => {
        setPreviewBlob(blob);
        setIsPreviewLoading(false);
      })
      .catch((error: unknown) => {
        setIsPreviewLoading(false);
        // The user already dismissed the preview — swallow the late error instead of surfacing it.
        if (!isOpenRef.current) {
          return;
        }
        setIsPreviewOpen(false);
        onError?.(error);
      });
  };

  return { previewBlob, isPreviewLoading, isPreviewOpen, openPreview, closePreview };
}
